import React, { useRef, useState } from 'react';
import { Button } from '@rocco/components/button/button';
import type {
    TileViewModel,
    TileableModel,
} from '@rocco/ui/tile-shared/view-models/tile';
import { CategoryPageTileWithAdminActions } from '../category-page/CategoryPageTileWithAdminActions';
import { createSectionScrollOnCollapse } from '../../utils/use-section-scroll-on-collapse';

interface Props {
    title: string;
    tiles: TileViewModel<TileableModel>[];
}

/**
 * Grid layout for featured professionals on project details page.
 * Visually identical to DetailsPageProductTileGrid but expects professional tiles
 */
export const DetailsPageProfessionalTileGrid = ({ title, tiles }: Props) => {
    const [expanded, setExpanded] = useState(false);
    const sectionRef = useRef<HTMLDivElement>(null);
    const tilesToShow = expanded ? tiles.length : Math.min(tiles.length, 8);

    const handleToggle = createSectionScrollOnCollapse(
        expanded,
        setExpanded,
        sectionRef
    );

    return (
        <>
            <div
                ref={sectionRef}
                className="mb-2 font-display text-20 font-semibold leading-[100%] text-[#3C3C3C]"
            >
                {title ? title : 'Professionals used'}
            </div>
            <div className="relative w-full border-t border-[#141414]/20">
                <div
                    className="mt-8.5 grid gap-x-[1.1rem] gap-y-18"
                    style={{
                        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                    }}
                >
                    {tiles.slice(0, tilesToShow).map((tile, idx) => (
                        <div key={idx} className="w-full">
                            <div className="h-full w-full overflow-hidden">
                                <CategoryPageTileWithAdminActions tile={tile} />
                            </div>
                        </div>
                    ))}
                </div>

                {tiles.length >= 9 && (
                    <div className="mt-30 flex justify-center">
                        <Button
                            onClick={handleToggle}
                            color="primary"
                            size="larger"
                            className="min-w-53.5"
                        >
                            {expanded ? 'View Less' : 'View More'}
                        </Button>
                    </div>
                )}
            </div>
        </>
    );
};
